import { useAiCreditsRemaining } from '@/composables/ai-upload/useAiCreditsRemaining';
import type { AppPageProps } from '@/types';
import type { ConfigurableProductPageObject } from '@/types/product';
import ShowAiGenerationCreditsRemainingController from '@/types/wayfinder/actions/App/Http/Controllers/Web/AiImageGeneration/ShowAiGenerationCreditsRemainingController';
import { useHttp, usePage } from '@inertiajs/vue3';

type CreditsRemainingResponse = {
    remaining: number;
    max: number;
    isDailyThresholdExceeded?: boolean;
    isUserDailyLimitExceeded?: boolean;
    hoursUntilNextCredit?: number | null;
};

type AiCreditsRefreshPageProps = AppPageProps & {
    product: ConfigurableProductPageObject;
};

export function useAiCreditsRefresh(): { refreshCreditsRemaining: () => Promise<void> } {
    const page = usePage<AiCreditsRefreshPageProps>();
    const { creditsRemaining, maxCredits, isDailyThresholdExceeded, isUserDailyLimitExceeded, hoursUntilNextCredit, pendingCredits } =
        useAiCreditsRemaining();
    const http = useHttp<Record<string, never>, CreditsRemainingResponse>();

    async function refreshCreditsRemaining(): Promise<void> {
        const url = ShowAiGenerationCreditsRemainingController({
            query: { group_identifier: page.props.product.group_identifier },
        }).url;

        try {
            await http.get(url, {
                onSuccess: (data: CreditsRemainingResponse) => {
                    // A pending value means mid-generation: the count intentionally shows remaining+1 until the first shot lands.
                    if (pendingCredits.value === undefined) {
                        creditsRemaining.value = data.remaining;
                    }
                    maxCredits.value = data.max;
                    isDailyThresholdExceeded.value = data.isDailyThresholdExceeded ?? false;
                    isUserDailyLimitExceeded.value = data.isUserDailyLimitExceeded ?? false;
                    hoursUntilNextCredit.value = data.hoursUntilNextCredit ?? undefined;
                },
            });
        } catch {
            // Credits count is a non-critical UI hint.
        }
    }

    return { refreshCreditsRemaining };
}
